import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";

import BriaBadge from "../../../common/BriaBadge/BriaBadge";
import styles from "./OverviewIntegrations.module.scss";

import LinkConstants from "../../../../constants/LinkConstants";
import RouterConstants from "../../../../constants/RouterConstants";

// Import popup components
import PhotoshopInstallationPopup from "../../../../pages/NewTG/Models/Popups/PhotoshopInstallationPopup/PhotoshopInstallationPopup";

// Icons
import DiscordIcon from "../../../../assets/Community/icons/discord.svg";
import AWSIcon from "../../../../assets/Integrations/icons/aws.svg";
import AzureIcon from "../../../../assets/Integrations/icons/azure.svg";
import ComfyUIIcon from "../../../../assets/Integrations/icons/comfyui.png";
import CopilotIcon from "../../../../assets/Integrations/icons/copilot.svg";
import FalIcon from "../../../../assets/Integrations/icons/fal.svg";
import FigmaIcon from "../../../../assets/Integrations/icons/figma.png";
import HuggingFaceIcon from "../../../../assets/Integrations/icons/huggingface.svg";
import InvokeIcon from "../../../../assets/Integrations/icons/invoke.svg";
import PhotoshopIcon from "../../../../assets/Integrations/icons/photoshop.png";
import PowerPointIcon from "../../../../assets/Integrations/icons/powerpoint.svg";
import ApiIcon from "../../../../assets/images/pngs/api-integration.png";
import DeepInfarIcon from "../../../../assets/images/pngs/deepinfra-logo.png";
import Github from "../../../../assets/images/pngs/github.png";
import IframeIcon from "../../../../assets/images/pngs/iframe-integration.png";
import ReplicateIcon from "../../../../assets/images/pngs/replicate logo.png";
import SegmindIcon from "../../../../assets/images/pngs/segmind-logo.png";

export type OverviewIntegrationOption =
	| "HUGGING_FACE"
	| "FAL"
	| "COMFY_UI"
	| "INVOKE"
	| "FIGMA"
	| "PHOTOSHOP"
	| "AWS"
	| "AZURE"
	| "COPILOT"
	| "POWERPOINT"
	| "REPLICATE"
	| "GITHUB"
	| "API"
	| "IFRAME"
	| "DEEPINFRA"
	| "SEGMIND"
	| "DISCORD";

export type IntegrationLinkCardProps = {
	icon: string;
	label?: string;
	link?: string;
	badgeLabel?: string;
	pageName?: string;
	openInNewTab?: boolean;
	onClick?: () => void;
	onFigmaPluginDownload?: () => void;
	onPhotoshopPluginDownload?: () => void;
	onFigmaNavigate?: () => void;
	onFigmaClick?: () => void;
	onShowPhotoshopPopup?: () => void;
};

type OverviewIntegrationsProps = {
	cards: OverviewIntegrationOption[];
	className?: string;
	pageName?: string;
	onFigmaClick?: () => void;
	onPhotoshopClick?: () => void;
	onFigmaPluginDownload?: () => void;
	onPhotoshopPluginDownload?: () => void;
	onComfyUClick?: () => void;
	onFalClick?: () => void;
	onInvokeClick?: () => void;
	onAWSClick?: () => void;
	onAzureClick?: () => void;
};

export const integrationsConfig: Record<OverviewIntegrationOption, IntegrationLinkCardProps> = {
	HUGGING_FACE: {
		icon: HuggingFaceIcon,
		link: LinkConstants.INTEGRATIONS.HUGGING_FACE,
	},
	FAL: {
		icon: FalIcon,
		link: LinkConstants.INTEGRATIONS.FAL,
	},
	COMFY_UI: {
		icon: ComfyUIIcon,
		link: LinkConstants.INTEGRATIONS.COMFY_UI,
	},
	INVOKE: {
		icon: InvokeIcon,
		link: LinkConstants.INTEGRATIONS.INVOKE,
	},
	FIGMA: {
		icon: FigmaIcon,
	},
	PHOTOSHOP: {
		icon: PhotoshopIcon,
		link: LinkConstants.ADOBE_MARKETPLACE,
	},
	AWS: {
		icon: AWSIcon,
		link: LinkConstants.INTEGRATIONS.AWS,
	},
	AZURE: {
		icon: AzureIcon,
		link: LinkConstants.INTEGRATIONS.AZURE,
	},
	COPILOT: {
		icon: CopilotIcon,
		link: LinkConstants.INTEGRATIONS.COPILOT,
		badgeLabel: "Soon",
	},
	POWERPOINT: {
		icon: PowerPointIcon,
		link: LinkConstants.INTEGRATIONS.POWERPOINT,
		badgeLabel: "Soon",
	},
	REPLICATE: {
		icon: ReplicateIcon,
		link: LinkConstants.INTEGRATIONS.REPLICATE,
	},
	GITHUB: {
		icon: Github,
		link: LinkConstants.INTEGRATIONS.GITHUB,
	},
	API: {
		icon: ApiIcon,
		link: LinkConstants.INTEGRATIONS.API,
	},
	IFRAME: {
		icon: IframeIcon,
		link: LinkConstants.INTEGRATIONS.IFRAME,
	},
	DEEPINFRA: {
		icon: DeepInfarIcon,
		link: LinkConstants.INTEGRATIONS.DEEPINFRA,
	},
	SEGMIND: {
		icon: SegmindIcon,
		link: LinkConstants.INTEGRATIONS.SEGMIND,
	},
	DISCORD: {
		icon: DiscordIcon,
		link: LinkConstants.DISCORD_COMMUNITY,
	},
};

export const OverviewIntegrations = ({
	cards,
	className,
	pageName,
	onFigmaClick,
	onPhotoshopClick,
	onPhotoshopPluginDownload,
	onComfyUClick,
	onFalClick,
	onInvokeClick,
	onAWSClick,
	onAzureClick,
}: OverviewIntegrationsProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "overviewIntegrations" });
	const navigate = useNavigate();

	const [showPhotoshopPopup, setShowPhotoshopPopup] = useState(false);

	const navigateToFigmaTab = () => {
		switch (pageName) {
			case "imageEditing.overview":
				navigate(RouterConstants.NEW_TG.FIGMA.fullPath);
				break;
			case "overview":
				navigate(RouterConstants.NEW_TG.FIGMA.fullPath);
				break;
			default:
				navigate(RouterConstants.NEW_TG.FIGMA.fullPath);
		}
	};

	const getFalLink = () => {
		switch (pageName) {
			case "imageGeneration.overview":
				return LinkConstants.INTEGRATIONS.IMAGE_GENERATION_FAL;
			case "imageEditing.overview":
				return LinkConstants.INTEGRATIONS.IMAGE_EDITING_FAL;
			case "productShot.overview":
				return LinkConstants.INTEGRATIONS.PRODUCT_SHOT_FAL;
			default:
				return LinkConstants.INTEGRATIONS.FAL;
		}
	};

	const getLogFunction = (integration: OverviewIntegrationOption) => {
		switch (integration) {
			case "COMFY_UI":
				return onComfyUClick;
			case "FAL":
				return onFalClick;
			case "INVOKE":
				return onInvokeClick;
			case "AWS":
				return onAWSClick;
			case "AZURE":
				return onAzureClick;
			case "FIGMA":
				return onFigmaClick;
			case "PHOTOSHOP":
				return onPhotoshopClick;
			default:
				return undefined;
		}
	};

	return (
		<Box className={`${styles.integrations} ${className}`}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<Box className={styles.cards}>
				{cards.map((key) => {
					const config = integrationsConfig[key];
					const label = t(`options.${key}.label`);

					const cardConfig = key === "FAL" ? { ...config, link: getFalLink() } : config;

					return (
						<IntegrationLinkCard
							key={key}
							{...cardConfig}
							label={label}
							pageName={pageName}
							onClick={getLogFunction(key)}
							onPhotoshopPluginDownload={onPhotoshopPluginDownload}
							onFigmaNavigate={navigateToFigmaTab}
							onFigmaClick={onFigmaClick}
							onShowPhotoshopPopup={() => setShowPhotoshopPopup(true)}
						/>
					);
				})}
			</Box>

			{/* Photoshop Installation Popup */}
			<PhotoshopInstallationPopup
				open={showPhotoshopPopup}
				onClose={() => setShowPhotoshopPopup(false)}
				onClick={() => {
					// Handle Photoshop plugin download/redirect
					window.open(LinkConstants.ADOBE_MARKETPLACE, "_blank");
					setShowPhotoshopPopup(false);
				}}
			/>
		</Box>
	);
};

const IntegrationLinkCard = ({
	icon,
	label,
	link,
	badgeLabel,
	pageName,
	onClick,
	onPhotoshopPluginDownload,
	onFigmaNavigate,
	onFigmaClick,
	onShowPhotoshopPopup,
	openInNewTab = true,
}: IntegrationLinkCardProps) => {
	const isFigma = label?.includes("Figma");
	const isPhotoshop = label?.includes("Photoshop");

	const handleClick = () => {
		onClick?.();
	};

	const cardContent = (
		<>
			<Box className={styles.iconContainer}>
				<img className={styles.icon} src={icon} alt={label} />
			</Box>
			<Typography className={styles.label}>{label}</Typography>
			{badgeLabel && <BriaBadge className={styles.badge} label={badgeLabel} />}
		</>
	);

	if (
		(pageName === "imageGeneration.overview" || pageName === "imageEditing.overview" || pageName === "overview") &&
		(isFigma || isPhotoshop)
	) {
		return (
			<Box
				onClick={() => {
					handleClick();
					if (isFigma) {
						onFigmaNavigate?.();
						onFigmaClick?.();
					} else if (isPhotoshop) {
						onShowPhotoshopPopup?.();
						onPhotoshopPluginDownload?.();
					}
				}}
				className={styles.clickableCard}
			>
				{cardContent}
			</Box>
		);
	}

	if (link) {
		return (
			<NavLink
				to={link}
				className={styles.clickableCard}
				target={openInNewTab ? "_blank" : undefined}
				rel={openInNewTab ? "noopener noreferrer" : undefined}
				onClick={handleClick}
			>
				{cardContent}
			</NavLink>
		);
	}

	return <Box className={styles.card}>{cardContent}</Box>;
};
